import { useCallback, useEffect, useRef, useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { X, ChevronDown } from "lucide-react";
import { Input } from "@/ui/atoms/Input";
import { cn, useDebounce } from "@/utils/utils";

/**
 * A search result option. `value`/`label` are required; callers may attach extra
 * fields (e.g. `{ code, name }`) which the component treats as opaque pass-through.
 */
export type TSearchableOption = { value: string; label: string } & Record<string, unknown>;

export interface TSearchableSelectProps {
    /** Controlled selection. Its `label` renders at rest with no search call. */
    value: TSearchableOption | null;
    /** Async search; only invoked once the input is focused/typed. */
    onSearch: (query: string) => Promise<TSearchableOption[]>;
    /** Called with the chosen option, or `null` when cleared. */
    onSelect: (option: TSearchableOption | null) => void;
    placeholder?: string;
    disabled?: boolean;
    /**
     * When `true`, the dropdown behaves like a browsable select: opening it runs an
     * empty-query search so the full (server-paginated) option list appears without the
     * user having to type first. Defaults to `false` (search only fires once a non-empty
     * query is typed).
     */
    searchOnOpen?: boolean;
    /** External loading override (e.g. while the caller resolves the seed value). */
    loading?: boolean;
    /** Marks the field invalid (sets `aria-invalid`). */
    error?: boolean;
    /** Associates a label via `htmlFor`/`id`. */
    id?: string;
    "aria-describedby"?: string;
}

type TSearchStatus = "idle" | "loading" | "ready" | "error";

export function SearchableSelect({
    value,
    onSearch,
    onSelect,
    placeholder = "Search...",
    disabled = false,
    searchOnOpen = false,
    loading: externalLoading = false,
    error = false,
    id,
    "aria-describedby": ariaDescribedBy,
}: TSearchableSelectProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<TSearchableOption[]>([]);
    const [status, setStatus] = useState<TSearchStatus>("idle");
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    /** Monotonic request counter; only the latest request's resolution is honoured. */
    const requestSeqRef = useRef(0);
    /** Stable ref to onSearch so the search effect doesn't re-run when the caller passes an inline lambda. */
    const onSearchRef = useRef(onSearch);
    useEffect(() => {
        onSearchRef.current = onSearch;
    });

    const debouncedQuery = useDebounce(query, 300);

    // Run the search whenever the debounced query changes while open. A sequence
    // counter discards any resolution (or rejection) that is not the latest request.
    useEffect(() => {
        if (disabled || !open) return;

        const trimmed = debouncedQuery.trim();
        // With `searchOnOpen`, an empty query still searches so the option list is browsable
        // on open. Otherwise an empty query clears results and waits for the user to type.
        if (trimmed === "" && !searchOnOpen) {
            setResults([]);
            setStatus("idle");
            setHighlightedIndex(-1);
            return;
        }

        const seq = ++requestSeqRef.current;
        setStatus("loading");

        onSearchRef
            .current(trimmed)
            .then((options) => {
                if (seq !== requestSeqRef.current) return; // stale response — discard
                setResults(options);
                setStatus("ready");
                setHighlightedIndex(options.length > 0 ? 0 : -1);
            })
            .catch(() => {
                if (seq !== requestSeqRef.current) return; // stale rejection — discard
                setResults([]);
                setStatus("error");
                setHighlightedIndex(-1);
            });
        // onSearch is intentionally read via ref — omitted from deps to avoid search storms when the caller passes an inline lambda
    }, [debouncedQuery, open, disabled, searchOnOpen]);

    // Click-outside closes and preserves the current value.
    useEffect(() => {
        if (!open) return;
        const handlePointerDown = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                closeAndReset();
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [open]);

    const closeAndReset = () => {
        setOpen(false);
        setQuery("");
        setResults([]);
        setStatus("idle");
        setHighlightedIndex(-1);
        // Invalidate any in-flight request so a late resolution cannot reopen results.
        requestSeqRef.current++;
    };

    // Keep wheel/touch scroll inside the dropdown list. When this Popover is portaled
    // to <body> inside a modal Radix Dialog, the Dialog's react-remove-scroll attaches a
    // bubble-phase `wheel`/`touchmove` listener on `document` that calls preventDefault on
    // any scroll originating outside the dialog — which blocks scrolling the option list.
    // Stopping propagation on the content keeps the event from ever reaching that listener,
    // so the browser scrolls the list natively.
    const contentCleanupRef = useRef<(() => void) | null>(null);
    const setContentNode = useCallback((node: HTMLDivElement | null) => {
        contentCleanupRef.current?.();
        contentCleanupRef.current = null;
        if (!node) return;
        const stopScrollPropagation = (event: Event) => event.stopPropagation();
        node.addEventListener("wheel", stopScrollPropagation, { passive: false });
        node.addEventListener("touchmove", stopScrollPropagation, { passive: false });
        contentCleanupRef.current = () => {
            node.removeEventListener("wheel", stopScrollPropagation);
            node.removeEventListener("touchmove", stopScrollPropagation);
        };
    }, []);

    // Open on click — NOT on focus. Focus-opening would auto-expand the dropdown
    // whenever a modal auto-focuses the field (Radix Dialog focuses the first focusable
    // element on open), and it cannot reopen after a selection anyway: the input keeps
    // focus, so the next click fires no focus event. Typing and ArrowDown also open.
    const handleInputClick = () => {
        if (disabled) return;
        setOpen(true);
    };

    const handleToggleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
        // Prevent the input from blurring (mirrors option mousedown).
        event.preventDefault();
        if (disabled) return;
        if (open) {
            closeAndReset();
        } else {
            inputRef.current?.focus();
            setOpen(true);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return;
        setOpen(true);
        setQuery(event.target.value);
    };

    const commit = (option: TSearchableOption) => {
        onSelect(option);
        closeAndReset();
    };

    const handleClear = () => {
        if (disabled) return;
        onSelect(null);
        closeAndReset();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                if (!open) {
                    setOpen(true);
                    return;
                }
                if (results.length > 0) {
                    setHighlightedIndex((i) => (i + 1) % results.length);
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                if (results.length > 0) {
                    setHighlightedIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
                }
                break;
            case "Enter": {
                // Selection-only: commit only a highlighted result, never free text.
                const highlighted = results[highlightedIndex];
                if (highlighted) {
                    event.preventDefault();
                    commit(highlighted);
                }
                break;
            }
            case "Escape":
                if (open) {
                    event.preventDefault();
                    closeAndReset();
                }
                break;
            default:
                break;
        }
    };

    const isLoading = externalLoading || status === "loading";
    // The input itself only ever holds the live query. At rest the selected label
    // renders as a separate visible text node (so it is queryable as text and avoids
    // a search firing just to display a seeded value).
    const showRestingLabel = !open && value !== null;

    return (
        <Popover.Root open={open} onOpenChange={(next) => !next && closeAndReset()}>
            <div ref={containerRef} className="relative">
                <Popover.Anchor asChild>
                    <div className="relative">
                        <Input
                            ref={inputRef}
                            id={id}
                            role="combobox"
                            aria-expanded={open}
                            aria-autocomplete="list"
                            aria-invalid={error}
                            aria-describedby={ariaDescribedBy}
                            autoComplete="off"
                            disabled={disabled}
                            error={error}
                            placeholder={showRestingLabel ? "" : placeholder}
                            value={open ? query : ""}
                            onClick={handleInputClick}
                            onChange={handleChange}
                            onKeyDown={handleKeyDown}
                            className={cn("pr-16", showRestingLabel && "text-transparent")}
                        />
                        {showRestingLabel ? (
                            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-foreground">
                                {value.label}
                            </span>
                        ) : null}
                        <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                            {value && !disabled ? (
                                <button
                                    type="button"
                                    aria-label="Clear selection"
                                    onClick={handleClear}
                                    className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            ) : null}
                            <button
                                type="button"
                                tabIndex={-1}
                                aria-label={open ? "Close options" : "Show options"}
                                disabled={disabled}
                                onMouseDown={handleToggleMouseDown}
                                className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none"
                            >
                                <ChevronDown
                                    className={cn(
                                        "h-4 w-4 transition-transform",
                                        open && "rotate-180",
                                    )}
                                />
                            </button>
                        </div>
                    </div>
                </Popover.Anchor>

                <Popover.Portal>
                    <Popover.Content
                        ref={setContentNode}
                        // `pointer-events-auto` keeps options clickable when this Popover is
                        // portaled to <body> inside a modal Radix Dialog, whose react-remove-scroll
                        // isolation sets `pointer-events: none` on everything outside the dialog.
                        className="pointer-events-auto z-[60] max-h-60 w-[var(--radix-popover-trigger-width)] overflow-auto rounded-lg border bg-popover p-1 shadow-lg"
                        sideOffset={4}
                        align="start"
                        // Keep focus in the input so typing is uninterrupted.
                        onOpenAutoFocus={(e) => e.preventDefault()}
                        onCloseAutoFocus={(e) => e.preventDefault()}
                        // The input/chevron live on the anchor, which Radix treats as
                        // *outside* the portaled content — without this guard every click
                        // on the field itself dismisses the popover (and, with the input
                        // still focused, it could then only be reopened by typing).
                        onInteractOutside={(event) => {
                            if (containerRef.current?.contains(event.target as Node)) {
                                event.preventDefault();
                            }
                        }}
                    >
                        {isLoading ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">Loading…</div>
                        ) : status === "error" ? (
                            <div className="px-3 py-2 text-sm text-destructive">
                                Error loading results
                            </div>
                        ) : status === "ready" && results.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                                No results
                            </div>
                        ) : (
                            <ul role="listbox" className="space-y-0.5">
                                {results.map((option, index) => (
                                    <li
                                        key={option.value}
                                        role="option"
                                        aria-selected={index === highlightedIndex}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                        onMouseDown={(e) => {
                                            // Prevent the input from blurring before we commit.
                                            e.preventDefault();
                                            commit(option);
                                        }}
                                        className={cn(
                                            "cursor-pointer rounded-md px-3 py-2 text-sm",
                                            index === highlightedIndex
                                                ? "bg-primary/10 text-primary"
                                                : "hover:bg-muted",
                                        )}
                                    >
                                        {option.label}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Popover.Content>
                </Popover.Portal>
            </div>
        </Popover.Root>
    );
}
